import { deviceAdminApi, enrollmentApi } from '@/api';
import { ButtonDropdown, Fieldset, HelpFactoryComponent, Select } from '@/components/common';
import { ControllerConflictWarningModal } from '@/components/enrollment/CredentialTemplateModal/CredentialTemplateModalTabs/CredentialTemplateModalFunctionsTab/ControllerConflictWarningModal/ControllerConflictWarningModal';
import { buildColumn, ColumnsProps, filterOption, handleResponse, removeAllNonNumberCharacters } from '@/Helper';
import { getPermissionErrorMessage, SecuredComponents, User } from '@/model/AccountModel';
import { BaseColumns } from '@/model/CommonModel';
import { CategoryType, ControlZone, DoorGroup, FunctionGroupConfig, FunctionGroupExtensionData, SelectOption } from '@/model/DeviceAdminModel';
import { ConflictMessage, FunctionCategory, FunctionModel, ThreatLevel, XboxController } from '@/model/EnrollmentModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { ArrowLeftOutlined, CaretDownOutlined } from '@ant-design/icons';
import { Breadcrumb, BreadcrumbProps, Button, Input, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TableRowSelection } from 'antd/es/table/interface';
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { batch } from 'react-redux';
import { DoorMasterModal } from '../../../DoorGroup/DoorMasterModal/DoorMasterModal';
import { DoorModal } from '../../../DoorGroup/DoorModal/DoorModal';
import styles from './DefineExtensionView.module.scss';

enum FunctionTypeEnum {
	CONTROL_ZONE,
	DOOR_GROUP,
	THREAT_LEVEL,
	INDEXED_COMMAND,
	SPECIAL_DOOR_GROUP,
}
enum DoorModalType {
	None = -1,
	DoorGroup = -2,
	MasterDoorGroup = -3,
}

type FunctionType = {
	functionGroupType: FunctionTypeEnum;
	controlZoneIndex?: number;
	controllerIndex?: number;
};

type DataSource = FunctionGroupExtensionData & Partial<BaseColumns>;

type Conflicts = {
	controllerId: number;
	controllerName: string;
	functionModel: FunctionModel;
	functionCategory: FunctionCategory;
};

type NewConflictItem = {
	key: string;
	functionModel: FunctionModel;
	functionCategory: FunctionCategory;
	controllerIds: number[];
};

type Props = {
	configurations: FunctionGroupConfig;
	functionGroupExtensionData: FunctionGroupExtensionData[];
	functionGroupExtensionId: string;
	isNew: boolean;
	helpTopicKey: string;
	defineExtensionChange: () => void;
	addCodeExtension: (addingData: FunctionGroupExtensionData[], extId: string, fnCat?: string) => void;
};

const user: User = getUser();
const noOfCustomAccessZones: number = user.noOfCustomAccessZones;
const doorGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Door_Groups);
const masterDoorGroupComponentPermission = User.getComponentPermission(user, SecuredComponents.Master_Door_Group);
const componentPermission = User.getComponentPermission(user, SecuredComponents.Function_Group_Extensions);

const defDgs: DoorGroup[] = [
	{
		ControllerIds: [],
		DoorGroupId: -1,
		IsMasterDoorGroup: false,
		Name: ' ',
		HasObjectPermission: false,
	},
];
if (doorGroupComponentPermission.canAdd) {
	defDgs.push({
		ControllerIds: [],
		DoorGroupId: -2,
		IsMasterDoorGroup: false,
		Name: `<${_('AddDoorGroup')}>`,
		HasObjectPermission: false,
	});
}
if (masterDoorGroupComponentPermission.canAdd) {
	defDgs.push({
		ControllerIds: [],
		DoorGroupId: -3,
		IsMasterDoorGroup: false,
		Name: `<${_('AddMasterDoorGroup')}>`,
		HasObjectPermission: false,
	});
}

const DefineExtensionView: React.FC<Props> = ({
	defineExtensionChange,
	addCodeExtension,
	isNew,
	configurations,
	functionGroupExtensionData,
	functionGroupExtensionId,
	helpTopicKey,
}) => {
	//#region state management variables
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

	const [functionCategories, setFunctionCategories] = useState<FunctionCategory[]>([]);
	const [selectedFnCatIndex, setSelectedFnCatIndex] = useState<number>(0);
	const [selectedFnGroupIndex, setSelectedFnGroupIndex] = useState<number>(0);
	const [selectedFnGroupIndexPrev, setSelectedFnGroupIndexPrev] = useState<number>(0);
	const [operatorsDoorGroups, setOperatorsDoorGroups] = useState<DoorGroup[]>([]);
	const [xboxControllers, setXboxControllers] = useState<XboxController[]>([]);
	const [controlZones, setControlZones] = useState<ControlZone[]>();
	const [indexedCommandOptions, setIndexedCommandOptions] = useState<SelectOption[]>([]);
	const [threatLevels, setThreatLevels] = useState<ThreatLevel[]>([]);

	const [controllerId, setControllerId] = useState<number>(-1);
	const [controlZoneId, setControlZoneId] = useState<number>(-1);
	const [doorGroup, setDoorGroup] = useState<string>('');
	const [showConflictMsgModal, setShowConflictMsgModal] = useState<boolean>(false);
	const [conflictMessages, setConflictMessages] = useState<ConflictMessage[]>([]);
	const [indexedCommandId, setIndexedCommandId] = useState<number>(1);
	const [threatLevel, setThreatLevel] = useState<number>(0);
	const [doorModal, setDoorModal] = useState<DoorModalType>(DoorModalType.None);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [fgeData, setFgeData] = useState<DataSource[]>([]);
	const [configs, setConfigs] = useState<FunctionGroupConfig>();
	const [fnGrpExtId, setFnGrpExtId] = useState<string>();
	const fetchedControlZones = new Map<number, ControlZone[]>();
	const [isEdit, setIsEdit] = useState(false);
	const clonedFgeData = useRef<DataSource[]>([]);
	const clonedFnCatIndex = useRef<number>(-1);
	const clonedFnGroupIndex = useRef<number>(-1);
	const clonedControllerIndex = useRef<number>(-1);
	const setClonedFgeData = (value: DataSource[], catIndex: number, groupIndex: number, controllerId: number) => {
		clonedFgeData.current = value;
		clonedFnCatIndex.current = catIndex;
		clonedFnGroupIndex.current = groupIndex;
		clonedControllerIndex.current = controllerId;
	};
	const isEditMode = useRef<boolean>(false);
	const setIsEditMode = (value: boolean) => {
		isEditMode.current = value;
		flushSync(() => setIsEdit(value));
	};
	//#endregion

	useEffect(() => {
		retrieveCategories();
		retrieveIndexedCommands();
		setConfigs(configurations);
		setFnGrpExtId(functionGroupExtensionId);
	}, []);

	useEffect(() => {
		if (threatLevels.length > 0 && indexedCommandOptions.length > 0) {
			setIsLoading(true);
			createTableDataSource()
				.then(response => setFgeData(response))
				.finally(() => setIsLoading(false));
		}
	}, [threatLevels.length, indexedCommandOptions.length]);

	useEffect(() => {
		retrieveControlZones(controllerId);
	}, [controllerId]);

	useEffect(() => {
		if (functionCategories?.length > 0 && doorGroup && !isEditMode.current) {
			if (selectedFnCatIndex == 4 && (selectedFnGroupIndex === 25 || selectedFnGroupIndex === 26 || selectedFnGroupIndex === 27)) {
				addToFunctionArray({
					functionGroupType: FunctionTypeEnum.SPECIAL_DOOR_GROUP,
				});
			} else {
				addToFunctionArray({
					functionGroupType: FunctionTypeEnum.DOOR_GROUP,
				});
			}
		}
	}, [doorGroup]);

	const createTableDataSource = async (): Promise<DataSource[]> => {
		let mappedData = [];
		for await (const item of functionGroupExtensionData) {
			let key = '';
			if (item.ControllersIds.length === 1) {
				key = buildUniqueIdDefault(FunctionTypeEnum.CONTROL_ZONE, item.AccessZoneOrControlZone, item.ControllersIds[0]);
			}
			if (item.DoorGroupId > 0 || item.MasterDoorGroupId > 0) {
				const isMasterDoorGroup = item.MasterDoorGroupId > 0;
				const doorGroup: DoorGroup = {
					ControllerIds: item.ControllersIds,
					DoorGroupId: isMasterDoorGroup ? item.MasterDoorGroupId : item.DoorGroupId,
					IsMasterDoorGroup: isMasterDoorGroup,
					Name: isMasterDoorGroup ? item.MasterDoorGroupName : item.DoorGroupName,
					HasObjectPermission: item.HasObjectPermission,
				};
				key = buildUniqueIdDoorGroup(FunctionTypeEnum.DOOR_GROUP, doorGroup);
			}

			let displayName = item.DisplayName;
			if (!displayName && !(item.DoorGroupName || item.MasterDoorGroupName)) {
				if (item.Function === 29) {
					const commandOption = indexedCommandOptions?.find(command => command.Id === item.AccessZoneOrControlZone);
					if (commandOption) {
						displayName = `${commandOption.Name} (${item.ControllerName})`;
					}
				} else if (item.Function === 41) {
					const threatLevel = threatLevels?.find(level => level.ThreatId === item.AccessZoneOrControlZone);
					if (threatLevel) {
						displayName = `${threatLevel.Name} (${item.ControllerName})`;
					}
				} else {
					const cControlZone = controlZones?.find(cz => cz.ControlZoneIndex === item.AccessZoneOrControlZone);
					if (cControlZone) {
						displayName = cControlZone.ControlZoneName != '' ? `${cControlZone?.ControlZoneName} (${item.ControllerName})` : item.ControllerName;
					} else if (item.ControllersIds.length > 0) {
						await deviceAdminApi.retrieveControlZones(item.ControllersIds[0]).then(response => {
							const cControlZone = response?.find(cz => cz.ControlZoneIndex === item.AccessZoneOrControlZone);
							if (cControlZone) {
								displayName = `${cControlZone?.ControlZoneName} (${item.ControllerName})`;
							}
						});
					}
				}
			}

			if (item.FunctionId !== -1) {
				mappedData.push({ ...item, key, DisplayName: (item.DoorGroupName || item.MasterDoorGroupName || displayName) ?? '' });
			}
		}

		return mappedData;
	};

	const retrieveCategories = async () => {
		try {
			setIsLoading(true);
			const apiPromises = await Promise.allSettled([
				enrollmentApi.getFunctionCategories(),
				enrollmentApi.getOperatorDoorGroups(),
				enrollmentApi.getXboxControllers(),
				enrollmentApi.getCredentialTemplateThreatLevels(),
			]);
			const categories = apiPromises[0];
			const doorGroups = apiPromises[1];
			const controllers = apiPromises[2];
			const threatLevels = apiPromises[3];
			if (categories.status === 'fulfilled') {
				const newCategories = categories.value.filter(item => {
					if (item.FunctionCategoryIndex === 4) {
						item.Functions = item.Functions.filter(subItem => subItem.FunctionIndex !== 28); // 28 is the index for DeadmanTimer
					}
					return item.FunctionCategoryIndex !== 5; // 5 is the index for functionGroup
				});
				setFunctionCategories(newCategories);
				setSelectedFnCatIndex(newCategories[0]?.FunctionCategoryIndex);
				setSelectedFnGroupIndex(newCategories[0]?.Functions[0]?.FunctionIndex);
			}
			if (doorGroups.status === 'fulfilled') {
				const dgs: DoorGroup[] = doorGroups.value?.length > 0 ? [...defDgs, ...doorGroups.value.sort((a, b) => a.Name.localeCompare(b.Name))] : defDgs;
				setOperatorsDoorGroups(dgs);
			}
			if (controllers.status === 'fulfilled') {
				if (controllers.value && controllers.value.length > 0) {
					const controllerId = controllers.value[0].ControllerId;
					setControllerIdState(controllerId);
				}
				setXboxControllers(controllers.value);
			}
			if (threatLevels.status === 'fulfilled') {
				const threatLevelsOptions: ThreatLevel[] = threatLevels.value?.map<ThreatLevel>(x => ({
					...x,
					Name: `${x.ThreatId.toLocaleString('en-US', { minimumIntegerDigits: 2 })} - ${x.Name}`,
				}));
				setThreatLevels(threatLevelsOptions);
			}
		} catch (error) {
			Logger.writeErrorLog(error);
		} finally {
			setIsLoading(false);
		}
	};

	const retrieveControlZones = async (controllerId: number) => {
		try {
			let controlZones: ControlZone[] = [];
			const currentControlZones = new Map(fetchedControlZones);
			if (currentControlZones.has(controllerId) && currentControlZones.get(controllerId)?.length > 0) {
				controlZones = currentControlZones.get(controllerId);
			} else {
				controlZones = await deviceAdminApi.retrieveControlZones(controllerId);
				currentControlZones.set(controllerId, controlZones || []);
			}
			setControlZones(controlZones);
		} catch (err) {
			Logger.writeErrorLog(err);
		}
		return [];
	};

	const retrieveIndexedCommands = async () => {
		try {
			const res = await deviceAdminApi.getIndexedCommands();
			if (!handleResponse(res)) {
				setIndexedCommandOptions(res.Entity);
			}
		} catch (err) {
			Logger.writeErrorLog(err);
		}
	};

	const filterDoorGroups = (functionsDataSource: Partial<DataSource>[], doorGroups: DoorGroup[]): DoorGroup[] => {
		let tempDg: DoorGroup[] = doorGroups?.length > 0 ? [...doorGroups] : [];
		if (functionsDataSource?.length > 0) {
			let clonedFunctionsDataSource = [...functionsDataSource];
			if (isEditMode.current) {
				const selectedFunction = clonedFunctionsDataSource.findIndex(x => x.key === selectedRowKeys[0]);
				if (selectedFunction !== -1) {
					clonedFunctionsDataSource.splice(selectedFunction, 1);
				}
			}
			clonedFunctionsDataSource.forEach(cf => {
				tempDg = tempDg.filter(lDg => {
					if (cf.MasterDoorGroupId > 0 && lDg.IsMasterDoorGroup) {
						if (cf.MasterDoorGroupName !== lDg.Name && cf.MasterDoorGroupId !== lDg.DoorGroupId) return lDg;
					} else {
						if (cf.DoorGroupName !== lDg.Name || cf.DoorGroupId !== lDg.DoorGroupId) return lDg;
					}
				});
			});
		}
		return tempDg;
	};

	const filterControllers = (functionsDataSource: Partial<DataSource>[], controllers: XboxController[]): XboxController[] => {
		let tempControllers: XboxController[] = controllers?.length > 0 ? [...controllers] : [];
		if (functionsDataSource?.length > 0) {
			let clonedFunctionsDataSource = [...functionsDataSource];
			if (isEditMode.current) {
				const selectedFunction = clonedFunctionsDataSource.findIndex(x => x.key === selectedRowKeys[0]);
				if (selectedFunction !== -1) {
					clonedFunctionsDataSource.splice(selectedFunction, 1);
				}
			}

			clonedFunctionsDataSource.forEach(cf => {
				if (cf.DoorGroupId === -1 && cf.MasterDoorGroupId === -1) {
					tempControllers = tempControllers.filter(controller => {
						if (cf.ControllersIds.length > 0) {
							return !cf.ControllersIds.some(x => x === controller.ControllerId);
						}

						return true;
					});
				}
			});
		}
		return tempControllers;
	};

	const addToFunctionArray = ({ functionGroupType, controlZoneIndex = -1, controllerIndex = -1 }: FunctionType, functionId?: number) => {
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === selectedFnCatIndex);
		const selectedFunction = selectedFunctionCategory.Functions.find(f => f.FunctionIndex === selectedFnGroupIndex);
		const newTableData: Partial<DataSource>[] = fgeData ? [...fgeData] : [];
		switch (functionGroupType) {
			case FunctionTypeEnum.DOOR_GROUP:
			case FunctionTypeEnum.SPECIAL_DOOR_GROUP:
				if (operatorsDoorGroups && operatorsDoorGroups?.length > 0) {
					const [_, strDoorGroupId, strIsMaster] = doorGroup.split('%');
					const doorGroupId = parseInt(strDoorGroupId);
					const boolIsMaster = strIsMaster === 'true';
					const selectedDoorGroup = operatorsDoorGroups.find(dg => dg.DoorGroupId === doorGroupId && dg.IsMasterDoorGroup === boolIsMaster);
					const newItem = {
						key: buildUniqueIdDoorGroup(functionGroupType, selectedDoorGroup),
						controllerIds: selectedDoorGroup.ControllerIds,
						functionModel: selectedFunction,
						functionCategory: selectedFunctionCategory,
					};

					if (selectedDoorGroup && doorGroupId > -1 && !checkConflicts(newTableData, newItem)) {
						newTableData.push({
							key: newItem.key,
							DoorGroupId: selectedDoorGroup.IsMasterDoorGroup === false ? doorGroupId : -1,
							DoorGroupName: selectedDoorGroup.IsMasterDoorGroup === false ? selectedDoorGroup.Name : '',
							FunctionId: functionId,
							Function: selectedFunction.FunctionIndex,
							FunctionName: selectedFunction.DefaultFunctionName,
							MasterDoorGroupId: selectedDoorGroup.IsMasterDoorGroup ? doorGroupId : -1,
							MasterDoorGroupName: selectedDoorGroup.IsMasterDoorGroup ? selectedDoorGroup.Name : '',
							DisplayName: selectedDoorGroup.Name,
							AccessZoneOrControlZone: 0,
							ControllerName: '',
							ControllersIds: newItem.controllerIds,
							HasObjectPermission: true,
						});
						setFgeData([...(newTableData as FunctionGroupExtensionData[])]);
					}
				}
				setDoorGroup('');
				break;

			case FunctionTypeEnum.INDEXED_COMMAND:
				if (xboxControllers?.length > 0) {
					const indexedCommandOption = indexedCommandOptions.find(option => option.Id === indexedCommandId);
					const cController = xboxControllers.find(c => c.ControllerId === controllerIndex);
					if (cController && indexedCommandOption) {
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdIndexedCommand(functionGroupType, indexedCommandOption, ControllerId),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								DoorGroupId: -1,
								DoorGroupName: '',
								Function: selectedFunction.FunctionIndex,
								FunctionName: selectedFunction.DefaultFunctionName,
								MasterDoorGroupId: -1,
								MasterDoorGroupName: '',
								AccessZoneOrControlZone: indexedCommandId,
								ControllerName: cController.ControllerName,
								SpecialCategoryName: indexedCommandOption.Name,
								ControllersIds: newItem.controllerIds,
								DisplayName: `${indexedCommandOption.Name} (${cController.ControllerName})`,
								HasObjectPermission: true,
							});
							setFgeData([...(newTableData as FunctionGroupExtensionData[])]);
							setControllerId(-1);
						}
					}
				}
				setControlZoneId(-1);
				break;

			case FunctionTypeEnum.THREAT_LEVEL:
				if (xboxControllers?.length > 0 && threatLevels?.length > 0) {
					const threatLevelOption = threatLevels.find(option => option.ThreatId === threatLevel);
					const cController = xboxControllers.find(c => c.ControllerId === controllerIndex);
					if (cController && threatLevelOption) {
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdThreatLevel(functionGroupType, threatLevelOption, ControllerId),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								DoorGroupId: -1,
								DoorGroupName: '',
								Function: selectedFunction.FunctionIndex,
								FunctionName: selectedFunction.DefaultFunctionName,
								MasterDoorGroupId: -1,
								MasterDoorGroupName: '',
								AccessZoneOrControlZone: threatLevel,
								ControllerName: cController.ControllerName,
								SpecialCategoryName: threatLevelOption.Name,
								ControllersIds: newItem.controllerIds,
								DisplayName: `${threatLevelOption.Name} (${cController.ControllerName})`,
								HasObjectPermission: true,
							});
							setFgeData([...(newTableData as FunctionGroupExtensionData[])]);
							setControllerId(-1);
						}
					}
				}
				setControlZoneId(-1);
				break;

			default:
				if (controlZoneIndex >= 0 && xboxControllers?.length > 0 && controlZones?.length > 0) {
					const cControlZone = controlZones.find(cz => cz.ControlZoneIndex === controlZoneIndex);
					const cController = xboxControllers.find(c => c.ControllerId === controllerIndex);
					if (cController && cControlZone) {
						const { ControlZoneIndex, ControlZoneName } = cControlZone;
						const { ControllerId } = cController;
						const newItem = {
							key: buildUniqueIdDefault(functionGroupType, ControlZoneIndex, ControllerId),
							controllerIds: [ControllerId],
							functionModel: selectedFunction,
							functionCategory: selectedFunctionCategory,
						};
						let fId = 0;
						const clonedFgeDataPoint = clonedFgeData.current.find(d => d.FunctionId === functionId);
						if (clonedFgeDataPoint) {
							if (
								selectedFnCatIndex === clonedFnCatIndex.current &&
								selectedFnGroupIndex === clonedFnGroupIndex.current &&
								controllerId === clonedControllerIndex.current &&
								ControlZoneIndex !== clonedFgeDataPoint.AccessZoneOrControlZone
							) {
								fId = functionId;
							}
						}
						if (!checkConflicts(newTableData, newItem)) {
							newTableData.push({
								key: newItem.key,
								DoorGroupId: -1,
								DoorGroupName: '',
								FunctionId: fId,
								Function: selectedFunction.FunctionIndex,
								FunctionName: selectedFunction.DefaultFunctionName,
								MasterDoorGroupId: -1,
								MasterDoorGroupName: '',
								AccessZoneOrControlZone: ControlZoneIndex,
								ControllerName: `${cController.ControllerName}`,
								ControllersIds: newItem.controllerIds,
								DisplayName: `${ControlZoneName} (${cController.ControllerName})`,
								TimeZoneName: ControlZoneName,
								HasObjectPermission: true,
							});
							setFgeData([...(newTableData as FunctionGroupExtensionData[])]);

							if (functionGroupType === FunctionTypeEnum.CONTROL_ZONE) {
								const filteredData = filterControllers(newTableData, xboxControllers);
								if (filteredData.length > 0) {
									setControllerId(filteredData[0].ControllerId);
								} else {
									setControllerId(-1);
								}
							}
						}
					}
				}
				setControlZoneId(-1);
				break;
		}
	};

	const onEntityAction = () => {
		enrollmentApi
			.getOperatorDoorGroups()
			.then(doorGroups => {
				const dgs: DoorGroup[] = doorGroups?.length > 0 ? [...defDgs, ...doorGroups.sort((a, b) => a.Name.localeCompare(b.Name))] : defDgs;
				setOperatorsDoorGroups(dgs);
			})
			.finally(() => {
				closeDGModal();
			});
	};

	const closeDGModal = () => {
		batch(() => {
			setDoorGroup('');
			setDoorModal(DoorModalType.None);
		});
	};

	const setControllerIdState = (controllerId: number): void => {
		setControllerId(controllerId);
	};

	const isAccessFunction = (functionIndex: number): boolean => {
		if (
			// Access
			[1, 2, 3, 34, 37, 39, 40].includes(functionIndex) ||
			// Password
			[20, 21, 22, 23, 24].includes(functionIndex) ||
			// Special
			[25, 26, 27].includes(functionIndex)
		) {
			return true;
		}

		return false;
	};

	const getErrorMessageForConflictControllers = (
		conflicts: {
			controllerId: number;
			controllerName: string;
			functionModel: FunctionModel;
			functionCategory: FunctionCategory;
		}[]
	): ConflictMessage[] => {
		const messages: ConflictMessage[] = conflicts.map<ConflictMessage>(conflicted => {
			const { functionModel, functionCategory } = conflicted;
			let { controllerName } = conflicted;
			let descriptorMsg = `${functionCategory.DefaultCategoryName}`;
			const catName = functionCategories.find(fc => fc.Functions.some(f => f.DefaultFunctionName === functionModel.FunctionName))?.DefaultCategoryName;
			if (!controllerName) {
				controllerName = xboxControllers.find(controller => controller.ControllerId === conflicted?.controllerId)?.ControllerName;
			}
			return {
				CategoryName: catName,
				FunctionName: functionModel.DefaultFunctionName,
				ControllerName: controllerName,
				Description: descriptorMsg,
			};
		});

		return messages;
	};

	const checkConflicts = (dataSource: Partial<DataSource>[], newItem: NewConflictItem): boolean => {
		let hasConflicts: boolean = false;
		const conflicts: Conflicts[] = [];
		if (isEditMode.current) {
			return hasConflicts;
		}

		const conflictedItems = dataSource.filter(x => x.ControllersIds.some(y => newItem.controllerIds.includes(y)));
		for (const controllerId of newItem.controllerIds) {
			const currentItemsCount = conflictedItems.flatMap(x => x.ControllersIds).filter(y => y === controllerId).length;
			for (const existing of conflictedItems) {
				if (existing.ControllersIds.includes(controllerId)) {
					const name = existing.DoorGroupName || existing.MasterDoorGroupName || existing.SpecialCategoryName || existing.TimeZoneName;
					if (configs?.OverlapAccessFlag) {
						if (currentItemsCount) {
							const { functionModel } = newItem;
							if (
								functionModel.FunctionIndex !== existing.Function ||
								(functionModel.FunctionIndex === existing.Function && !isAccessFunction(functionModel.FunctionIndex)) ||
								(functionModel.FunctionIndex === existing.Function &&
									isAccessFunction(existing.Function) &&
									currentItemsCount > noOfCustomAccessZones)
							) {
								hasConflicts = true;
								let item = conflicts.find(x => x.controllerId === controllerId && x.functionModel.FunctionIndex === existing.Function);
								if (!item) {
									item = {
										functionCategory: {
											CategoryName: existing.FunctionName,
											DefaultCategoryName: name,
											FunctionCategoryIndex: existing.FunctionId,
										},
										functionModel: {
											DefaultFunctionName: existing.FunctionName,
											FunctionIndex: existing.Function,
											FunctionName: existing.FunctionName,
										},
										controllerId,
										controllerName: existing.ControllerName,
									};
									conflicts.push(item);
								} else {
									item.functionCategory.DefaultCategoryName += `, ${name}`;
								}
							}
						}
					} else {
						if (currentItemsCount && existing.key !== newItem.key) {
							hasConflicts = true;
							let item = conflicts.find(x => x.controllerId === controllerId && x.functionModel.FunctionIndex === existing.Function);
							if (!item) {
								item = {
									functionCategory: {
										CategoryName: existing.FunctionName,
										DefaultCategoryName: name,
										FunctionCategoryIndex: existing.FunctionId,
									},
									functionModel: {
										DefaultFunctionName: existing.FunctionName,
										FunctionIndex: existing.Function,
										FunctionName: existing.FunctionName,
									},
									controllerId,
									controllerName: existing.ControllerName,
								};
								conflicts.push(item);
							} else {
								item.functionCategory.DefaultCategoryName += `, ${name}`;
							}
						}
					}
				}
			}
		}

		if (hasConflicts) {
			if (isEditMode.current) {
				setFgeData([...clonedFgeData.current]);
			}
			setConflictMessages(getErrorMessageForConflictControllers(conflicts));
			setShowConflictMsgModal(true);
		}

		return hasConflicts;
	};

	const handleOnChangeFunctionCategory = (stringValue: string) => {
		const value: number = Number(stringValue);
		const selectedFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === value);
		setSelectedFnCatIndex(selectedFunctionCategory.FunctionCategoryIndex);
		setSelectedFnGroupIndex(selectedFunctionCategory.Functions[0].FunctionIndex);

		if (value === 1 || value === 2) {
			const filteredData = filterControllers(fgeData, xboxControllers);
			if (filteredData.length > 0) {
				setControllerId(filteredData[0].ControllerId);
			} else {
				setControllerId(-1);
			}
		}
	};

	const handleOnChangeFunction = (stringValue: string) => {
		const value: number = Number(stringValue);
		if (selectedFnCatIndex == 4 && (value === 29 || value === 41)) {
			setControllerId(-1);
		}
		setSelectedFnGroupIndex(value);
	};

	const removeTableData = (keys: React.Key[]) => {
		if (fgeData && fgeData.length > 0) {
			const filteredSelectedRowKeys = keys.filter(item => !selectedRowKeys.some(internKey => internKey === item));
			setSelectedRowKeys(filteredSelectedRowKeys);
			const filteredData = fgeData.filter(item => !keys.some(key => key === item.key));
			setFgeData([...filteredData]);
		}
	};

	const handleOnChangeDoorGroup = (value: string) => {
		const [_, doorGroupId] = value.split('%');
		const id = parseInt(doorGroupId);
		if (id < -1) {
			setDoorModal(id);
		} else {
			closeDGModal();
			setDoorGroup(value);
		}
	};

	const columns: ColumnsProps<DataSource>[] = [
		{
			...buildColumn(_('Function'), 'FunctionName', '15em', 'start'),
		},
		{
			...buildColumn(_('GroupZoneName'), 'DisplayName', 'auto', 'start'),
		},
	];

	const controlZoneDropdownComponent = (handleOnChange: (value: number) => void) => (
		<>
			<label htmlFor='controlZone'>{_('ControlZone')}:</label>
			{controlZones && (
				<Select
					notUseCurrentParse
					id='controlZone'
					className={styles.select}
					getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
					onChange={handleOnChange}
					options={controlZones.map(cz => ({
						key: cz.ControlZoneIndex,
						label: cz.ControlZoneName,
						value: cz.ControlZoneIndex,
					}))}
					style={{ width: 505 }}
					value={controlZoneId > -1 ? controlZoneId : ''}
					showSearch={true}
					filterOption={filterOption}
					virtual={true}
				/>
			)}
		</>
	);

	const controllerDropdownComponent = (fgeData: Partial<DataSource>[], handleOnChange: (value: number) => void) => {
		const filteredData = filterControllers(fgeData, xboxControllers);
		return (
			<>
				<label htmlFor='controller'>{_('Controller')}:</label>
				<Select
					notUseCurrentParse
					id='controller'
					className={styles.select}
					getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
					onChange={handleOnChange}
					options={filteredData.map(controller => ({
						key: controller.ControllerId,
						label: controller.ControllerName,
						value: controller.ControllerId,
					}))}
					style={{ width: 505 }}
					value={controllerId > -1 ? controllerId : ''}
					showSearch={true}
					filterOption={filterOption}
					virtual={true}
				/>
			</>
		);
	};

	const handleOnChangeControlZone = (value: number): void => {
		setControlZoneId(value);
		if (!isEditMode.current && functionCategories?.length > 0 && value > -1) {
			addToFunctionArray({
				functionGroupType: FunctionTypeEnum.CONTROL_ZONE,
				controlZoneIndex: value,
				controllerIndex: controllerId,
			});
		}
	};

	const buildUniqueIdDoorGroup = (functionType: FunctionTypeEnum, doorGroup: DoorGroup): string => {
		return `${functionType}%${doorGroup.DoorGroupId}%${doorGroup.IsMasterDoorGroup}`;
	};

	const buildUniqueIdThreatLevel = (functionType: FunctionTypeEnum, threatLevel: ThreatLevel, controllerId: number): string => {
		return `${functionType}%${threatLevel.ThreatId}%${controllerId}`;
	};

	const buildUniqueIdIndexedCommand = (functionType: FunctionTypeEnum, indexedCommand: SelectOption, controllerId: number): string => {
		return `${functionType}%${indexedCommand.Id}%${controllerId}`;
	};

	const buildUniqueIdDefault = (functionType: FunctionTypeEnum, index: number, controllerId: number): string => {
		return `${functionType}%${index}%${controllerId}`;
	};

	const renderFunctionGroupLayout = (): JSX.Element => {
		let dd: JSX.Element = null;
		if ((xboxControllers && xboxControllers.length > 0 && selectedFnCatIndex == 1) || selectedFnCatIndex == 2) {
			dd = (
				<>
					<div className={cx(styles.sections, styles.functionSections)}>{controllerDropdownComponent(fgeData, setControllerIdState)}</div>
					<div className={cx(styles.sections, styles.functionSections)}>
						{controlZoneDropdownComponent((value: number) => handleOnChangeControlZone(value))}
					</div>
				</>
			);
		} else if (selectedFnCatIndex == 4 && (selectedFnGroupIndex === 29 || selectedFnGroupIndex === 41)) {
			switch (selectedFnGroupIndex) {
				case 29: // Indexed command
					dd = (
						<div className={styles.indexedCommand}>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='indexedCommandDropdown'>{_('IndexedCommand')}:</label>
								<Select
									notUseCurrentParse
									id='indexedCommandDropdown'
									className={styles.select}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setIndexedCommandId(value)}
									options={indexedCommandOptions.map(option => ({
										key: option.Id,
										label: option.Name,
										value: option.Id,
									}))}
									style={{ width: 505 }}
									value={indexedCommandId}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent(
									fgeData,
									(value: number) => {
										setControllerIdState(Number(value));
										if (!isEditMode.current) {
											addToFunctionArray({
												functionGroupType: FunctionTypeEnum.INDEXED_COMMAND,
												controllerIndex: value,
											});
										}
									}
								)}
							</div>
						</div>
					);
					break;

				case 41: // Threat level
					dd = (
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='threatLevelDropdown'>{_('SetThreatLevel')}:</label>
								<Select
									notUseCurrentParse
									id='threatLevelDropdown'
									className={styles.select}
									getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
									onChange={(value: number) => setThreatLevel(value)}
									options={threatLevels.map((threatLevel: ThreatLevel) => ({
										key: threatLevel.ThreatId,
										label: threatLevel.Name,
										value: threatLevel.ThreatId,
									}))}
									style={{ width: 505 }}
									value={threatLevel}
									showSearch={true}
									filterOption={filterOption}
									virtual={true}
								/>
							</div>
							<div className={cx(styles.sections, styles.functionSections)}>
								{controllerDropdownComponent(
									fgeData,
									(value: number) => {
										setControllerIdState(value);
										if (!isEditMode.current) {
											addToFunctionArray({
												functionGroupType: FunctionTypeEnum.THREAT_LEVEL,
												controllerIndex: value,
											});
										}
									}
								)}
							</div>
						</div>
					);
					break;
			}
		} else {
			const tempDgs = filterDoorGroups(fgeData, operatorsDoorGroups);
			dd = (
				<div className={cx(styles.sections, styles.functionSections)}>
					<label htmlFor='doorGroup'>{_('DoorGroup')}:</label>
					<div>
						<Select
							className={styles.select}
							id='doorGroup'
							getPopupContainer={(trigger: HTMLElement) => trigger.parentElement}
							onChange={handleOnChangeDoorGroup}
							options={tempDgs.map((dg: DoorGroup) => {
								const key = buildUniqueIdDoorGroup(FunctionTypeEnum.DOOR_GROUP, dg);
								return {
									key,
									label: dg.Name || ' ',
									title: dg.Name,
									value: key,
								};
							})}
							style={{ width: 505 }}
							value={doorGroup}
							showSearch={true}
							filterOption={filterOption}
							virtual={true}
						/>
					</div>
				</div>
			);
		}

		return dd;
	};

	const getFunctionOptions = (): FunctionModel[] => {
		let options: FunctionModel[] = [];
		if (functionCategories?.length > 0 && selectedFnCatIndex > -1) {
			const lFunctionCategory = functionCategories.find(fc => fc.FunctionCategoryIndex === selectedFnCatIndex);
			if (lFunctionCategory) options = lFunctionCategory.Functions;
		}
		return options;
	};

	const renderDoorModal = (): JSX.Element => {
		if (doorModal === DoorModalType.DoorGroup) {
			return <DoorModal onEntityAction={onEntityAction} onSetVisible={closeDGModal} setShouldResetSearchColumn={() => {}} />;
		}
		if (doorModal === DoorModalType.MasterDoorGroup) {
			return <DoorMasterModal onEntityAction={onEntityAction} onSetVisible={closeDGModal} setShouldResetSearchColumn={() => {}} />;
		}
		return null;
	};

	const handleOnChange = (selectedRows: React.Key[]) => {
		let newSelection: React.Key[] = [];
		if (selectedRows.length > selectedRowKeys.length) {
			newSelection.push(...selectedRows);
		} else {
			newSelection = selectedRowKeys.filter(key => selectedRows.some(item => key === item));
		}

		setSelectedRowKeys(newSelection);
	};

	const rowSelection: TableRowSelection = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: false,
		onChange: handleOnChange,
		selectedRowKeys,
		getCheckboxProps: (record: DataSource) => ({
			id: `extensionCheckbox-${record.key}`,
			title: record.HasObjectPermission ? 'Your role does not have permission to delete this entry' : '',
			disabled: isEditMode.current || !record.HasObjectPermission,
		}),
	};

	const handleOnClickOption = (key: string) => {
		const selectedItem = fgeData?.filter(x => x.key === selectedRowKeys[0])[0];

		if (key === 'edit') {
			setIsEditMode(true);
			const category = functionCategories.find(category => category.Functions.some(x => x.FunctionIndex === selectedItem.Function));
			if (category) {
				setSelectedFnCatIndex(category.FunctionCategoryIndex);
				setSelectedFnGroupIndex(selectedItem.Function);
				setSelectedFnGroupIndexPrev(selectedItem.Function);
				setClonedFgeData([...fgeData], category.FunctionCategoryIndex, selectedItem.Function, controllerId);
				if ((xboxControllers && xboxControllers.length > 0 && category.FunctionCategoryIndex == 1) || category.FunctionCategoryIndex == 2) {
					setControlZoneId(selectedItem.AccessZoneOrControlZone);
					setControllerId(selectedItem.ControllersIds[0]);
				} else if (category.FunctionCategoryIndex == 4 && (selectedItem.Function === 29 || selectedItem.Function === 41)) {
					switch (selectedItem.Function) {
						case 29: // Indexed command
							setIndexedCommandId(selectedItem.AccessZoneOrControlZone);
							setControllerId(selectedItem.ControllersIds[0]);
							break;

						case 41: // Threat level
							setThreatLevel(selectedItem.AccessZoneOrControlZone);
							setControllerId(selectedItem.ControllersIds[0]);
							break;
					}
				} else {
					const isMasterDoorGroup = selectedItem.DoorGroupId === -1;
					const doorGroupId = isMasterDoorGroup ? selectedItem.MasterDoorGroupId : selectedItem.DoorGroupId;
					const doorGroupName = isMasterDoorGroup ? selectedItem.MasterDoorGroupName : selectedItem.DoorGroupName;
					const selectedDoorGroup = {
						DoorGroupId: doorGroupId,
						IsMasterDoorGroup: isMasterDoorGroup,
						ControllerIds: selectedItem.ControllersIds,
						Name: doorGroupName,
						HasObjectPermission: true,
					};
					const isSpecialDoorGroup =
						category.FunctionCategoryIndex == 4 && (selectedItem.Function === 25 || selectedItem.Function === 26 || selectedItem.Function === 27);

					setOperatorsDoorGroups(operatorsDoorGroups);
					flushSync(() => {
						setDoorGroup(
							buildUniqueIdDoorGroup(isSpecialDoorGroup ? FunctionTypeEnum.SPECIAL_DOOR_GROUP : FunctionTypeEnum.DOOR_GROUP, selectedDoorGroup)
						);
					});
				}
			}
		}
		if (key === 'delete') {
			if (selectedRowKeys.length > 0) {
				removeTableData(selectedRowKeys);
			}
		}
	};

	const hasMoreThanOne = selectedRowKeys.length > 1;

	const tableOptions = {
		labelOrIcon: '...',
		options: [
			{
				id: 'functionGroupsEditButton',
				label: _('Edit'),
				disabled: !componentPermission.canUpdate || hasMoreThanOne || isEditMode.current,
				title: getPermissionErrorMessage(componentPermission.canUpdate, false, hasMoreThanOne),
				value: 'edit',
			},
			{
				id: 'functionGroupsDeleteButton',
				label: _('Delete'),
				title: getPermissionErrorMessage(true, true, hasMoreThanOne),
				value: 'delete',
			},
		],
	};

	const handleOnUpdateFunction = () => {
		let fId: number = 0;
		setIsEditMode(false);
		flushSync(() => {
			setFgeData(prevState => {
				const insertIndex: number = prevState.findIndex(x => x.key === selectedRowKeys[0]);
				if (insertIndex !== -1) {
					fId = prevState[insertIndex].FunctionId;
					prevState.splice(insertIndex, 1);
				}

				return [...prevState];
			});
		});

		if ((xboxControllers && xboxControllers.length > 0 && selectedFnCatIndex == 1) || selectedFnCatIndex == 2) {
			addToFunctionArray(
				{
					functionGroupType: FunctionTypeEnum.CONTROL_ZONE,
					controlZoneIndex: controlZoneId,
					controllerIndex: controllerId,
				},
				fId
			);
		} else if (selectedFnCatIndex == 4 && (selectedFnGroupIndex === 29 || selectedFnGroupIndex === 41)) {
			switch (selectedFnGroupIndex) {
				case 29: // Indexed command
					addToFunctionArray({
						functionGroupType: FunctionTypeEnum.INDEXED_COMMAND,
						controllerIndex: controllerId,
					});
					break;

				case 41: // Threat level
					addToFunctionArray({
						functionGroupType: FunctionTypeEnum.THREAT_LEVEL,
						controllerIndex: controllerId,
					});
					break;
			}
		} else {
			fId = selectedFnGroupIndex !== selectedFnGroupIndexPrev ? 0 : fId;
			if (selectedFnCatIndex == 4 && (selectedFnGroupIndex === 25 || selectedFnGroupIndex === 26 || selectedFnGroupIndex === 27)) {
				addToFunctionArray(
					{
						functionGroupType: FunctionTypeEnum.SPECIAL_DOOR_GROUP,
					},
					fId
				);
			} else {
				addToFunctionArray(
					{
						functionGroupType: FunctionTypeEnum.DOOR_GROUP,
					},
					fId
				);
			}
		}
		setSelectedRowKeys([]);
	};

	const handleOnCancelEdit = () => {
		setIsEditMode(false);
		handleOnChangeFunctionCategory('0');
		setDoorGroup('');
		setControlZoneId(-1);
	};

	const goBackToFunctionGroup = () => {
		setIsEditMode(false);
		setFgeData([]);
		setFnGrpExtId('');
		defineExtensionChange();
		handleOnChangeFunctionCategory('0');
		setDoorGroup('');
		setControlZoneId(-1);
	};

	const items: BreadcrumbProps['items'] = [
		{
			title: (
				<a
					onClick={() => {
						if (!isEditMode.current) {
							goBackToFunctionGroup();
						}
					}}
					className={isEditMode.current ? styles.disabledLink : ''}>
					<ArrowLeftOutlined />
					<label id='go-back-label' className={styles.linkText}>
						{_('GoBackToFunctionGroup')}
					</label>
				</a>
			),
		},
	];

	return (
		<Spin spinning={isLoading} tip={`${_('Loading')}...`}>
			<div className={styles.container}>
				{showConflictMsgModal && (
					<ControllerConflictWarningModal
						conflicts={conflictMessages}
						overlapAccessFlag={configs.OverlapAccessFlag}
						cCMVersion={configs.CCMVersionSupportsHRecord}
						customAccessZones={configs.CustomAccessZones}
						onOkClick={() => {
							setShowConflictMsgModal(false);
							setConflictMessages([]);
						}}
					/>
				)}
				<div className={styles.form} id='functionDDContainer'>
					<div className={styles.navigationContainer}>
						<Breadcrumb items={items} />
						<HelpFactoryComponent helpFeature={HelpFeature.FunctionGroups} topicId={helpTopicKey} size='medium' />
					</div>
					<div className={styles.formFieldInstructions}>
						<div>
							<p className={styles.sectionTitle}>{_('DefineExtension')}</p>
							<label className={styles.extensionDefine}>
								{`${_('ExtensionDigits')}:`}
								<Input
									className={styles.extensionDefineInput}
									onChange={e => {
										setFnGrpExtId(removeAllNonNumberCharacters(e.currentTarget.value));
									}}
									maxLength={2}
									value={fnGrpExtId}
									disabled={!isNew}
								/>
								{`(${_('Blank')}, 1 ${_('or')} 2 ${_('digits')})`}
							</label>
						</div>
						<p className={styles.selectInstructions}>{`${_('SelectFunction')}.`}</p>
						<Fieldset title={`${_('Instructions')}:`} className={styles.instructionContainer}>
							<span>{`${_('ToDefineThisExtension')}:`}</span>
							<ol className={styles.instructionsList}>
								<li>{_('SelectExtensionDigits')}</li>
								<li>{_('AddOneOrMoreFunctions')}</li>
								<li>{isNew ? _('PressAddToFinishDefiningThis') : _('PressUpdateToFinishDefiningThis')}</li>
							</ol>
							<span>{_('ScramblePadUsePinCode')}</span>
						</Fieldset>
					</div>
					<div className={styles.formFieldsContainer}>
						<div>
							<div className={cx(styles.sections, styles.functionSections)}>
								<label htmlFor='functionCategory'>{_('FunctionCategory')}:</label>
								<Select
									className={styles.select}
									id='functionCategory'
									onChange={handleOnChangeFunctionCategory}
									options={functionCategories?.map(fc => ({
										key: fc.FunctionCategoryIndex?.toString(),
										label: fc.DefaultCategoryName,
										value: fc.FunctionCategoryIndex?.toString(),
									}))}
									style={{ width: 240 }}
									value={selectedFnCatIndex?.toString()}
								/>
							</div>
							<div className={styles.functionSectionsContainer}>
								<div className={cx(styles.sections, styles.functionSections)}>
									<label htmlFor='functionS'>{_('Function')}:</label>
									<Select
										className={styles.select}
										id='functionS'
										onChange={handleOnChangeFunction}
										options={getFunctionOptions().map(f => ({
											key: f.FunctionIndex?.toString(),
											label: f.DefaultFunctionName,
											value: f.FunctionIndex?.toString(),
										}))}
										style={{ width: 240 }}
										value={selectedFnGroupIndex?.toString()}
									/>
								</div>
							</div>
						</div>
					</div>
					<div id='ddContainer' className={styles.functionValueContainer}>
						<div>{renderFunctionGroupLayout()}</div>
						<div className={styles.buttons}>
							{isEditMode.current ? (
								<>
									<Button type='primary' onClick={handleOnUpdateFunction}>
										{_('Update')}
									</Button>
									<Button onClick={handleOnCancelEdit}>{_('Cancel')}</Button>
								</>
							) : (
								<></>
							)}
						</div>
					</div>
					<div>
						<ButtonDropdown
							id={'functionGroupsTableActionDropdown'}
							menuOptions={tableOptions.options}
							icon={<CaretDownOutlined />}
							labelIcon={tableOptions.labelOrIcon}
							onClickOption={handleOnClickOption}
							disabled={selectedRowKeys.length === 0 || isEditMode.current}
						/>
					</div>
					<Table
						className={styles.functionsTable}
						rowClassName={(record: DataSource, _) => {
							let className: string;
							if (!record.HasObjectPermission) {
								className = styles.disabled;
							}
							return className;
						}}
						columns={columns as ColumnsType}
						dataSource={fgeData}
						id={'credentialFunctionsTable'}
						pagination={false}
						scroll={{ y: '280px' }}
						size='small'
						rowSelection={rowSelection}
					/>
					<div className={styles.addCodeExtensionContainer}>
						<Button
							type='primary'
							className={styles.addCodeExtensionButton}
							disabled={(!componentPermission.canUpdate && !isNew) || (!componentPermission.canAdd && isNew) || isEditMode.current}
							onClick={() => {
								let catName = functionCategories.find(item => item.FunctionCategoryIndex === selectedFnCatIndex)?.CategoryName;
								if (selectedFnGroupIndex === 4) {
									catName = CategoryType.Alarm;
								} else if (selectedFnGroupIndex === 11) {
									catName = CategoryType.Relay;
								}

								addCodeExtension(fgeData, fnGrpExtId, catName);
								defineExtensionChange();
							}}>
							{`${isNew ? _('Add') : _('Update')} ${_('CodeExtension')}`}
						</Button>
						<Button
							id='functionGroupCancelButton'
							key='cancel'
							className={styles.cancelCodeExtensionButton}
							onClick={() => defineExtensionChange()}
							disabled={isEditMode.current}>
							{_('Cancel')}
						</Button>
					</div>
				</div>
				{renderDoorModal()}
			</div>
		</Spin>
	);
};

export { DefineExtensionView };
